import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { UserProfile } from '../../../shared/interfaces/role.interface';

@Component({
  selector: 'app-policies',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    TitleComponent,
    RouterOutlet,
    LoaderComponent,
  ],
  templateUrl: './policies.component.html',
  styleUrls: ['./policies.component.css', '../../../shared/styles/layout.css'],
})
export class PoliciesComponent implements OnDestroy {
  title: string = 'Políticas';
  subtitle: string = 'Gestiona las políticas y permisos de los usuarios.';
  isUuid: boolean = false;
  myroles: any = {}; // Change from array to object

  // For managing subscriptions
  private destroy$ = new Subject<void>();
  private routerSubscription: Subscription | null = null;
  private dataLoaded = false;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private sanitizer: DomSanitizer,
    private storageService: StorageService
  ) {
    // Setup router event listener once in the constructor
    this.setupRouterEvents();
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);

  displayedColumns: string[] = ['name', 'description', 'acciones'];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta política?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.rolesService.deleteRole(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dataSource.data = this.dataSource.data.filter(
            (user) => user.id !== id
          );
        }
      });
  }

  getRoles() {
    return this.rolesService.getAllRoles();
  }

  loadRoles() {
    // Use the identifier 'users' instead of the Spanish name 'Usuarios'
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'policies',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      // console.log(
      //   'No user roles found in storage or no permissions for users'
      // );
    }
  }

  setupRouterEvents() {
    // Setup router subscription only once
    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        // Update the isUuid flag based on URL
        this.updateViewState();

        // Only load data when we're on the main policies page AND data isn't already loaded
        if (!this.isUuid && !this.dataLoaded) {
          this.loadData();
        }
      });
  }

  updateViewState() {
    const currentUrl = this.router.url;
    if (
      currentUrl.includes('/users/policies/edit') ||
      currentUrl.includes('/users/policies/add')
    ) {
      this.isUuid = true;
    } else if (currentUrl.includes('/users/policies')) {
      this.isUuid = false;
      // Set dataLoaded to false when returning to main view to force reload
      if (this.dataLoaded) {
        this.dataLoaded = false;
      }
    }
    this.cdr.detectChanges();
  }

  ngOnInit() {
    // Check if we're on the main policies page
    if (!this.isUuid && !this.dataLoaded) {
      this.loadData();
    }

    this.loadRoles();
  }

  loadData() {
    if (this.dataLoaded) return; // Prevent duplicate loading

    this.loaderService.showLoader();

    // Load roles data
    this.getRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          this.dataSource.data = roles;
          this.dataLoaded = true;
          this.loaderService.hideLoader();
        },
        error: (err) => {
          console.error(err);
          this.loaderService.hideLoader();
        },
      });

    this.dataSource.paginator = this.paginator;
  }

  goToRole(id: number) {
    this.router.navigate(['/home/users/policies/edit', id]);
    this.isUuid = true;
  }

  addPolicy() {
    this.router.navigate(['/home/users/policies/add']);
    this.isUuid = true;
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // Method to sanitize HTML content
  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
